import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Search, X, Mail, Phone, MapPin, StickyNote, Trash2 } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

function fmtAddress(a) {
  if (!a) return '';
  return [a.line1, a.line2, a.suburb, a.city, a.region, a.postcode, a.country].filter(Boolean).join(', ');
}

function CustomerDetail({ email, onClose, onDeleted }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/shop/customers/${encodeURIComponent(email)}`);
        setData(data);
        setNotes(data.customer?.notes || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load customer');
      } finally { setLoading(false); }
    })();
  }, [email]);

  const saveNotes = async () => {
    try {
      await api.post('/admin/shop/customers/notes', { email, notes });
      toast.success('Notes saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const removeCustomer = async () => {
    const paidCount = data?.customer?.paid_orders_count || 0;
    const warning = paidCount > 0
      ? `\n\n\u26A0\uFE0F  This customer has ${paidCount} paid order(s). Deleting will PERMANENTLY remove those order records too.`
      : '';
    if (!window.confirm(`Delete ${email} and ALL of their ${data?.customer?.orders_count || 0} order(s) permanently? This cannot be undone.${warning}`)) return;
    try {
      const res = await api.delete(`/admin/shop/customers/${encodeURIComponent(email)}`);
      toast.success(`Deleted ${email} and ${res.data.orders_deleted || 0} order(s)`);
      if (onDeleted) onDeleted();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="customer-detail">
        <div className="px-6 py-4 border-b flex items-start justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div>
            <div className="h-eyebrow">Customer</div>
            <div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{data?.customer?.name || email}</div>
            <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={removeCustomer} className="text-xs inline-flex items-center gap-1 rounded-md px-2 py-1 border" style={{ color: '#9A2C2C', borderColor: '#9A2C2C' }} data-testid="customer-delete" title="Delete customer and all their orders">
              <Trash2 size={12} /> Delete
            </button>
            <button onClick={onClose} className="p-1" aria-label="Close" data-testid="customer-close" style={{ color: 'var(--heritage-plum)' }}><X size={20} /></button>
          </div>
        </div>

        {loading || !data ? (
          <div className="p-6 h-eyebrow">Loading...</div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="grid md:grid-cols-3 gap-4">
              <Panel className="p-4">
                <div className="h-eyebrow text-[10px]">Lifetime value</div>
                <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{fmt(data.customer.ltv)} {data.customer.currency}</div>
                <div className="text-xs opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>{data.customer.paid_orders_count} paid · {data.customer.orders_count} total</div>
              </Panel>
              <Panel className="p-4">
                <div className="h-eyebrow text-[10px]">Contact</div>
                <div className="mt-2 text-sm space-y-1.5" style={{ color: 'var(--heritage-plum)' }}>
                  <div className="flex items-center gap-2"><Mail size={12} /> <a href={`mailto:${email}`} className="hover:underline">{email}</a></div>
                  {data.customer.phone && <div className="flex items-center gap-2"><Phone size={12} /> {data.customer.phone}</div>}
                </div>
              </Panel>
              <Panel className="p-4">
                <div className="h-eyebrow text-[10px] flex items-center gap-1"><MapPin size={11} /> Last shipping address</div>
                <div className="text-xs mt-2" style={{ color: 'var(--heritage-plum)' }}>{fmtAddress(data.customer.shipping_address) || '—'}</div>
              </Panel>
            </div>

            <div>
              <div className="h-eyebrow flex items-center gap-1"><StickyNote size={11} /> Admin notes</div>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes only — not shared with the customer."
                className="w-full rounded-md border px-3 py-2 text-sm bg-white mt-1"
                style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                data-testid="customer-notes"
              />
              <button onClick={saveNotes} className="h-btn-outline text-xs mt-2" data-testid="customer-notes-save">Save notes</button>
            </div>

            <div>
              <div className="h-eyebrow">Order history</div>
              <ul className="mt-2 divide-y" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                {data.orders.map((o) => (
                  <li key={o.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</div>
                      <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{new Date(o.created_at).toLocaleString('en-NZ')} · {(o.items || []).length} items</div>
                    </div>
                    <div className="text-sm text-right" style={{ color: 'var(--heritage-plum)' }}>
                      <div className="h-serif">{fmt(o.total)} {o.currency}</div>
                      <div className="text-[10px] uppercase tracking-widest opacity-80">{o.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminShopCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shop/customers');
      setCustomers(data.customers || []);
    } catch { toast.error('Failed to load customers'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return customers;
    return customers.filter((c) =>
      (c.email || '').toLowerCase().includes(s) || (c.name || '').toLowerCase().includes(s),
    );
  }, [customers, q]);

  return (
    <div className="space-y-5" data-testid="admin-shop-customers">
      <div className="h-panel-thin p-6">
        <div className="h-eyebrow">Store</div>
        <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Customers</div>
        <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Everyone who has bought from the Heritage store. Auto-generated from orders.</p>
      </div>

      <Panel className="p-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-2.5 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email"
            className="pl-8 pr-3 py-1.5 rounded-full text-sm w-full" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }}
            data-testid="customers-search" />
        </div>
      </Panel>

      {loading && <div className="h-eyebrow">Loading...</div>}
      {!loading && filtered.length === 0 && (
        <Panel className="p-8 text-center">
          <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>No customers yet</div>
          <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Once shoppers place their first order, they will appear here.</div>
        </Panel>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Panel key={c.email} className="p-4 cursor-pointer hover:shadow-md transition" onClick={() => setOpen(c.email)} data-testid={`customer-card-${c.email}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="h-serif text-base truncate" style={{ color: 'var(--heritage-plum)' }}>{c.name || c.email}</div>
                <div className="text-xs opacity-75 truncate" style={{ color: 'var(--heritage-plum)' }}>{c.email}</div>
              </div>
              <span className="h-chip text-[10px]" style={{ background: 'rgba(75,24,56,0.08)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{c.orders_count} order{c.orders_count === 1 ? '' : 's'}</span>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <div>
                <div className="h-eyebrow text-[10px]">Lifetime value</div>
                <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{fmt(c.ltv)} {c.currency}</div>
              </div>
              <div className="text-xs opacity-75 text-right" style={{ color: 'var(--heritage-plum)' }}>
                Last order<br /><span className="h-serif">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString('en-NZ') : '—'}</span>
              </div>
            </div>
            {c.notes && <div className="text-[11px] opacity-70 mt-2 italic" style={{ color: 'var(--heritage-plum)' }}>Note: {c.notes.substring(0, 60)}{c.notes.length > 60 ? '...' : ''}</div>}
          </Panel>
        ))}
      </div>

      {open && <CustomerDetail email={open} onClose={() => { setOpen(null); load(); }} onDeleted={load} />}
    </div>
  );
}
